/**
 * EnrichmentOpportunitiesTab
 *
 * Main tab component for viewing and managing enrichment opportunities.
 * Shows missing product attributes with cost estimates and research actions.
 *
 * Part of Story 345: Enrichment Opportunities Dashboard
 *
 * Date: 2026-01-06
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  RefreshCw,
  Loader2,
  CheckCircle2,
  Search,
  AlertTriangle,
  TrendingUp,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { useToast } from '@/components/ui/use-toast';
import {
  getOpportunities,
  getCategories,
  EnrichmentOpportunity,
  OpportunityFilters,
  OpportunityStatus,
  PriceTier,
} from '@/services/enrichmentOpportunityService';
import { OpportunityCard } from './OpportunityCard';
import { ResearchConfirmDialog } from './ResearchConfirmDialog';
import { ResearchProgressPanel } from './ResearchProgressPanel';

interface GroupedOpportunities {
  category: string;
  opportunities: EnrichmentOpportunity[];
}

export function EnrichmentOpportunitiesTab() {
  const { selectedSite } = useSite();
  const { toast } = useToast();

  // State
  const [opportunities, setOpportunities] = useState<EnrichmentOpportunity[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // Filters
  const [status, setStatus] = useState<OpportunityStatus | 'all'>('pending');
  const [category, setCategory] = useState<string>('all');
  const [priceTier, setPriceTier] = useState<PriceTier | 'all'>('all');
  const [sortBy, setSortBy] = useState<'recommendation' | 'impact' | 'cost' | 'products'>('recommendation');

  // Research dialog
  const [selectedOpportunity, setSelectedOpportunity] = useState<EnrichmentOpportunity | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [researchingOpportunityId, setResearchingOpportunityId] = useState<string | null>(null);

  // Load opportunities
  const loadOpportunities = useCallback(async () => {
    if (!selectedSite?.id) return;

    setLoading(true);
    try {
      const filters: OpportunityFilters = {
        sortBy,
        limit: 100,
      };

      if (status !== 'all') filters.status = status;
      if (category !== 'all') filters.category = category;
      if (priceTier !== 'all') filters.priceTier = priceTier;

      const result = await getOpportunities(selectedSite.id, filters);
      setOpportunities(result.opportunities);
      setTotal(result.total);
    } catch (error) {
      console.error('Failed to load opportunities:', error);
      toast({
        title: 'Error',
        description: 'Failed to load enrichment opportunities',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [selectedSite?.id, status, category, priceTier, sortBy, toast]);

  // Load categories
  const loadCategories = useCallback(async () => {
    if (!selectedSite?.id) return;

    try {
      const cats = await getCategories(selectedSite.id);
      setCategories(cats);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }, [selectedSite?.id]);

  useEffect(() => {
    loadOpportunities();
    loadCategories();
  }, [loadOpportunities, loadCategories]);

  // Group opportunities by category
  const groupedOpportunities: GroupedOpportunities[] = React.useMemo(() => {
    const groups = new Map<string, EnrichmentOpportunity[]>();

    for (const opp of opportunities) {
      const cat = opp.category;
      if (!groups.has(cat)) {
        groups.set(cat, []);
      }
      groups.get(cat)!.push(opp);
    }

    return Array.from(groups.entries()).map(([category, opportunities]) => ({
      category,
      opportunities,
    }));
  }, [opportunities]);

  // Handlers
  const handleResearchClick = (opportunity: EnrichmentOpportunity) => {
    setSelectedOpportunity(opportunity);
    setConfirmDialogOpen(true);
  };

  const handleResearchConfirmed = (opportunityId: string) => {
    setConfirmDialogOpen(false);
    setResearchingOpportunityId(opportunityId);
    // Update local state
    setOpportunities(prev =>
      prev.map(o => (o.id === opportunityId ? { ...o, status: 'researching' as OpportunityStatus } : o))
    );
  };

  const handleResearchComplete = () => {
    setResearchingOpportunityId(null);
    loadOpportunities();
  };

  const handleIgnore = async (opportunityId: string) => {
    // Update local state
    setOpportunities(prev => prev.filter(o => o.id !== opportunityId));
    setTotal(prev => prev - 1);
  };

  const handleMarkAllNA = async (opportunityId: string) => {
    // Update local state
    setOpportunities(prev => prev.filter(o => o.id !== opportunityId));
    setTotal(prev => prev - 1);
  };

  // Empty state
  if (!loading && opportunities.length === 0) {
    return (
      <Card className="p-8 text-center">
        <CheckCircle2 className="h-12 w-12 mx-auto text-green-500" />
        <h3 className="mt-4 text-lg font-medium">No enrichment opportunities found</h3>
        <p className="text-muted-foreground mt-2">
          {status === 'pending'
            ? 'All products have complete attribute data, or no recent pipeline runs have identified gaps.'
            : `No opportunities with status "${status}" found.`}
        </p>
        {status !== 'all' && (
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => setStatus('all')}
          >
            Show All Statuses
          </Button>
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Status:</span>
          <Select value={status} onValueChange={(v) => setStatus(v as OpportunityStatus | 'all')}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="researching">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="ignored">Ignored</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Category:</span>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Price Tier:</span>
          <Select value={priceTier} onValueChange={(v) => setPriceTier(v as PriceTier | 'all')}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tiers</SelectItem>
              <SelectItem value="premium">Premium Only</SelectItem>
              <SelectItem value="mid">Mid-Tier</SelectItem>
              <SelectItem value="entry">Entry Level</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Sort:</span>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recommendation">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Recommendation
                </div>
              </SelectItem>
              <SelectItem value="impact">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Highest Impact
                </div>
              </SelectItem>
              <SelectItem value="products">
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Most Products
                </div>
              </SelectItem>
              <SelectItem value="cost">Cost</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1" />

        <Button
          variant="outline"
          size="sm"
          onClick={loadOpportunities}
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      {/* Summary */}
      <div className="text-sm text-muted-foreground">
        Showing {opportunities.length} of {total} opportunities
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Opportunity groups */}
      {!loading && (
        <div className="space-y-6">
          {groupedOpportunities.map(({ category, opportunities: catOpps }) => (
            <div key={category} className="space-y-3">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg">{category}</h3>
                <Badge variant="secondary">{catOpps.length}</Badge>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {catOpps.map((opp) => (
                  <React.Fragment key={opp.id}>
                    {researchingOpportunityId === opp.id ? (
                      <ResearchProgressPanel
                        opportunityId={opp.id}
                        attributeName={opp.attributeName}
                        productCount={opp.productCount}
                        onComplete={handleResearchComplete}
                      />
                    ) : (
                      <OpportunityCard
                        opportunity={opp}
                        onResearch={handleResearchClick}
                        onIgnore={handleIgnore}
                        onMarkAllNA={handleMarkAllNA}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Research confirmation dialog */}
      {selectedOpportunity && (
        <ResearchConfirmDialog
          open={confirmDialogOpen}
          onOpenChange={setConfirmDialogOpen}
          opportunity={selectedOpportunity}
          onConfirm={handleResearchConfirmed}
        />
      )}
    </div>
  );
}
